/**
 * Hook for product identifier search (Remake → Produkt-Brücke).
 *
 * Wraps GET /api/v2/products/search via searchProducts API client.
 * Disabled when identifier is empty so the user can clear the input
 * without triggering requests.
 *
 * Identifier changes are debounced by 250ms so rapid keystrokes do not
 * spam the backend.
 */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { searchProducts, type ProductSearchType } from '@/lib/api/products-search'

function useDebouncedString(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(id)
  }, [value, delayMs])
  return debounced
}

export function useProductsSearch(identifier: string, type: ProductSearchType = 'auto') {
  const debounced = useDebouncedString(identifier, 250)
  return useQuery({
    queryKey: ['products-search', debounced, type],
    queryFn: () => searchProducts({ identifier: debounced, type }),
    enabled: debounced.length > 0,
    staleTime: 30_000,
  })
}
